import { useMemo, useState } from 'react';
import type { Folder } from '../../api/folders.js';

/**
 * Pick a destination folder (or "Top level") for a template or folder
 * being moved. Disallows picking a descendant of the source so folder
 * moves can't create a cycle.
 */
export interface MoveToFolderDialogProps {
  sourceLabel: string;
  folders: ReadonlyArray<Folder>;
  foldersById: Map<string, Folder>;
  /** When moving a folder, its own id + its descendants are invalid
   *  destinations. When moving a template we don't need this. */
  disallowId?: string | undefined;
  onCancel(): void;
  onChoose(folderId: string | null): void | Promise<void>;
}

export function MoveToFolderDialog(props: MoveToFolderDialogProps): JSX.Element {
  const { sourceLabel, folders, foldersById, disallowId, onCancel, onChoose } = props;
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const descendantsOfDisallow = useMemo(() => {
    if (!disallowId) return new Set<string>();
    const out = new Set<string>([disallowId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        if (f.parentId && out.has(f.parentId) && !out.has(f.id)) {
          out.add(f.id);
          grew = true;
        }
      }
    }
    return out;
  }, [disallowId, folders]);

  const tree = useMemo(() => buildPaths(folders, foldersById), [folders, foldersById]);

  async function pick(id: string | null): Promise<void> {
    setBusy(true);
    try {
      await onChoose(id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <header className="modal-header">
          <h2>Move {sourceLabel}</h2>
          <button type="button" className="btn-icon" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <button
            type="button"
            className={`move-dialog-row ${selected === '__TOP__' ? 'move-dialog-row--selected' : ''}`}
            onClick={() => setSelected('__TOP__')}
          >
            <span className="move-dialog-path">Top level</span>
          </button>
          {tree.map((row) => (
            <button
              key={row.id}
              type="button"
              className={`move-dialog-row ${selected === row.id ? 'move-dialog-row--selected' : ''} ${descendantsOfDisallow.has(row.id) ? 'move-dialog-row--disabled' : ''}`}
              disabled={descendantsOfDisallow.has(row.id)}
              onClick={() => setSelected(row.id)}
              title={row.path}
            >
              <span className="move-dialog-path">{row.path}</span>
            </button>
          ))}
        </div>
        <footer className="modal-footer">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || selected === null}
            onClick={() => pick(selected === '__TOP__' ? null : selected)}
          >
            {busy ? 'Moving…' : 'Move here'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function buildPaths(
  folders: ReadonlyArray<Folder>,
  byId: Map<string, Folder>,
): Array<{ id: string; path: string }> {
  const path = (id: string | null): string => {
    const chain: string[] = [];
    let cursor: string | null = id;
    while (cursor) {
      const f = byId.get(cursor);
      if (!f) break;
      chain.unshift(f.name);
      cursor = f.parentId;
    }
    return chain.join(' / ');
  };
  return folders
    .map((f) => ({ id: f.id, path: path(f.id) }))
    .sort((a, b) => a.path.localeCompare(b.path));
}
